import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, AlertTriangle, Flame, ListChecks, Sparkles, ArrowRight } from "lucide-react";
import OpsTopBar from "../../components/ops/OpsTopBar";
import QueueRow from "../../components/ops/QueueRow";
import { OperatorAvatar } from "../../components/ops/BranchAvatar";
import { useOpsRole } from "../../context/OpsRoleContext";
import { useOpsUI } from "../../components/ops/OpsLayout";
import useApi from "../../hooks/useApi";

const priColor = { high: "#D0526B", medium: "#B8892B", low: "#6B6E62" };

export default function OpsToday() {
  const { role } = useOpsRole();
  const { openAI } = useOpsUI();
  const { data: summary } = useApi(`/ops/summary?role=${role}`, [role]);
  const { data: queue } = useApi(`/ops/queue?scope=today&role=${role}`, [role]);
  const tasks = queue?.tasks || [];
  const op = summary?.operator;

  const cards = [
    { l: "Scheduled", v: summary?.scheduled, icon: ListChecks, c: "#16180F" },
    { l: "Completed", v: summary?.completed, icon: CheckCircle2, c: "#5F8213" },
    { l: "Remaining", v: summary?.remaining, icon: Clock, c: "#2F6BFF" },
    { l: "High priority", v: summary?.high_priority, icon: Flame, c: "#D0526B" },
    { l: "Overdue", v: summary?.overdue, icon: AlertTriangle, c: "#D0526B" },
    { l: "Attention", v: summary?.attention, icon: AlertTriangle, c: "#B8892B" },
  ];

  return (
    <>
      <OpsTopBar title="Today's Managed Social Queue"
        subtitle={role === "operator" && op ? `${op.name} · ${summary?.daily_plan?.date || ""}` : summary?.daily_plan?.date} />
      <div className="p-6 sm:p-8 max-w-[1400px] space-y-8">

        {/* Workload summary */}
        <section className="card p-6" data-testid="ops-workload">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <span className="label-caps">Today's workload</span>
              <div className="flex items-center gap-2 mt-1">
                {role === "operator" && op && <OperatorAvatar initials={op.initials} color={op.color} size={28} />}
                <h2 className="text-xl font-bold tracking-tight text-ink">{summary?.completed ?? 0} of {summary?.scheduled ?? 0} actions complete</h2>
              </div>
            </div>
            <div className="w-full sm:w-80">
              <div className="flex justify-between text-xs text-ink-2 mb-1"><span>Progress</span><span className="font-bold text-ink">{summary?.progress ?? 0}%</span></div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
                <motion.div className="h-full rounded-full" style={{ background: "var(--grad-green)" }} initial={{ width: 0 }} animate={{ width: `${summary?.progress ?? 0}%` }} transition={{ duration: 0.8, ease: "easeOut" }} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
            {cards.map((c) => (
              <div key={c.l} className="rounded-xl p-4" style={{ background: "#F6F8F1" }} data-testid={`workload-${c.l.toLowerCase().replace(/ /g, "-")}`}>
                <c.icon size={16} strokeWidth={1.75} style={{ color: c.c }} />
                <div className="text-3xl font-extrabold tracking-tighter text-ink mt-2">{c.v ?? "—"}</div>
                <div className="text-xs text-ink-2 mt-0.5">{c.l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* AI daily plan + ask */}
        <section className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 card p-6 relative overflow-hidden" style={{ background: "var(--grad-ink)" }} data-testid="ops-daily-plan">
            <div className="absolute -right-14 -top-14 w-56 h-56 rounded-full" style={{ background: "rgba(147,192,32,.12)" }} />
            <div className="relative">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#B6E14F" }}>AI daily plan · {summary?.daily_plan?.date}</span>
              <h3 className="text-2xl font-extrabold tracking-tight text-white mt-2">{summary?.daily_plan?.recommended_actions ?? 0} managed actions recommended</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                {(summary?.daily_plan?.breakdown || []).map((b) => (
                  <div key={b.label} className="rounded-xl px-3 py-2.5" style={{ background: "rgba(255,255,255,.06)" }}>
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: priColor[b.priority] }} /><span className="text-2xl font-extrabold tracking-tighter text-white">{b.count}</span></div>
                    <div className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,.55)" }}>{b.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button onClick={openAI} className="card card-hover p-6 text-left flex flex-col justify-between" data-testid="ops-today-ask-ai">
            <span className="grid place-items-center w-11 h-11 rounded-xl" style={{ background: "var(--grad-green-soft)" }}><Sparkles size={20} color="#5F8213" strokeWidth={1.75} /></span>
            <div className="mt-4">
              <h3 className="font-bold tracking-tight text-ink">Not sure where to start?</h3>
              <p className="text-sm text-ink-2 mt-1">Ask Big Growth AI what to prioritise right now.</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold mt-3" style={{ color: "#5F8213" }}>Ask the assistant <ArrowRight size={14} strokeWidth={2} /></span>
            </div>
          </button>
        </section>

        {/* Queue */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold tracking-tight text-ink">Priority queue</h2>
            <Link to="/ops/queue" className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "#5F8213" }} data-testid="ops-view-full-queue">View full queue <ArrowRight size={14} strokeWidth={2} /></Link>
          </div>
          <div className="card overflow-hidden divide-y" style={{ borderColor: "#E7E8E4" }} data-testid="ops-today-queue">
            {tasks.length === 0 && <div className="p-8 text-center text-ink-2 text-sm">Nothing left in today's queue. 🎉</div>}
            {tasks.map((t) => <QueueRow key={t.id} task={t} />)}
          </div>
        </section>
      </div>
    </>
  );
}
